import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, Button, Chip, Tab, Tabs,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
  IconButton, Tooltip, ToggleButtonGroup, ToggleButton, Link,
  FormControl, InputLabel, Select
, Snackbar, Alert, Autocomplete } from '@mui/material';
import Layout from '../components/Layout';
import api from '../services/api';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import { useAuth } from '../context/AuthContext';

const fmt = v => '$' + Number(v||0).toLocaleString('en-US', { minimumFractionDigits: 2 });
const fmtDate = d => d ? new Date(d).toLocaleDateString() : '—';

const STATUS = {
  pending:   { bg: '#f5f5f5', color: '#555', label: 'Pending' },
  in_progress: { bg: '#e3f2fd', color: '#1565c0', label: 'In Progress' },
  completed: { bg: '#fff3e0', color: '#e65100', label: 'Completed' },
  invoiced:  { bg: '#e8eaf6', color: '#3949ab', label: 'Invoiced' },
  paid:      { bg: '#e8f5e9', color: '#2e7d32', label: 'Paid' },
  submitted: { bg: '#fff3e0', color: '#e65100', label: 'Submitted' },
  approved:  { bg: '#e8f5e9', color: '#2e7d32', label: 'Approved' },
};

export default function AccountsReceivable() {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', payment_amount: '', due_date: '', sequence_order: 1, amount_mode: 'fixed', pct: '' });
  const [saving, setSaving] = useState(false);
  const [termsDialog, setTermsDialog] = useState({ open: false, milestoneId: null });
  const [invoiceSuccess, setInvoiceSuccess] = useState(null);
  const [expandedRows, setExpandedRows] = useState({}); // {milestoneId: true} // {invoice_id, invoice_number, customer_email, sending:false, sent:false}
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [invTerms, setInvTerms] = useState('net_30');

  useEffect(() => {
    api.get('/contracts').then(r => {
      const signed = (Array.isArray(r.data) ? r.data : []).filter(c =>
        ['active','completed'].includes(c.status)
      );
      setContracts(signed);
      if (signed.length > 0) setSelectedContract(signed[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedContract) return;
    setLoading(true);
    api.get(`/milestones/contract/${selectedContract}`)
      .then(r => setMilestones(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedContract]);

  const contract = contracts.find(c => c.id === selectedContract);
  const totalValue = contract ? Number(contract.contract_amount) : 0;
  const totalBilled = milestones.filter(m => ['invoiced','paid'].includes(m.status)).reduce((s,m) => s+Number(m.payment_amount),0);
  const totalPaid = milestones.filter(m => m.status==='paid').reduce((s,m) => s+Number(m.payment_amount),0);
  // Milestone allocation guard: sum of ALL existing milestones must stay within contract value
  const allocated = milestones.reduce((sum, m) => sum + Number(m.payment_amount || 0), 0);
  const remaining = Math.max(0, totalValue - allocated);
  const newAmount = form.amount_mode === 'pct'
    ? (totalValue * Number(form.pct || 0)) / 100
    : Number(form.payment_amount || 0);
  const wouldExceed = totalValue > 0 && (allocated + newAmount) > totalValue + 0.005;
  const totalOutstanding = totalBilled - totalPaid;
  const pct = totalValue > 0 ? Math.round((totalBilled/totalValue)*100) : 0;

  const handleMarkComplete = async (id) => {
    await api.put(`/milestones/${id}/status`, { status: 'completed' });
    setMilestones(ms => ms.map(m => m.id===id ? {...m, status:'completed'} : m));
  };

  const handlePreviewInvoice = async (id, payment_terms = 'net_30') => {
    try {
      // responseType blob: the shared api instance defaults to JSON; the
      // preview endpoint streams binary PDF, which corrupts without this.
      const r = await api.post(`/milestones/${id}/invoice-preview`, { payment_terms }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { alert('Failed to generate invoice preview'); }
  };

  const handleIssueInvoice = async (id, payment_terms = 'net_30') => {
    try {
      const r = await api.post(`/milestones/${id}/invoice`, { payment_terms });
      setMilestones(ms => ms.map(m => m.id===id ? {...m, status:'invoiced', invoice_number: r.data.invoice?.invoice_number, invoice_status: 'submitted', invoice_document_url: r.data.invoice?.invoice_document_url} : m));
      setInvoiceSuccess({
        invoice_id: r.data.invoice?.id,
        invoice_number: r.data.invoice?.invoice_number,
        invoice_document_url: r.data.invoice?.invoice_document_url,
        customer_email: r.data.invoice?.customer_email || null,
        recipient_email: r.data.invoice?.recipient_email || r.data.invoice?.customer_email || null,
        sending: false, sent: false,
      });
    } catch(e) { alert(e.response?.data?.error || 'Failed to issue invoice'); }
  };

  const handleDelete = async (id) => {
    await api.delete(`/milestones/${id}`);
    setMilestones(ms => ms.filter(m => m.id !== id));
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      const resolvedAmount = form.amount_mode === 'pct'
        ? Number(((totalValue * Number(form.pct || 0)) / 100).toFixed(2))
        : Number(form.payment_amount);
      await api.post('/milestones', {
        title: form.title,
        description: form.description,
        due_date: form.due_date,
        sequence_order: form.sequence_order,
        payment_amount: resolvedAmount,
        contract_id: selectedContract
      });
      const r = await api.get(`/milestones/contract/${selectedContract}`);
      setMilestones(Array.isArray(r.data) ? r.data : []);
      setAddDialog(false);
      setForm({ title: '', description: '', payment_amount: '', due_date: '', sequence_order: milestones.length + 1, amount_mode: 'fixed', pct: '' });
    } catch(e) {}
    setSaving(false);
  };

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', fontSize: 32 }}>Accounts Receivable</Typography>
          <Typography variant="body2" color="text.secondary">Manage payment milestones and track invoices</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 360 }}
            options={contracts}
            value={contracts.find(c => c.id === selectedContract) || null}
            onChange={(e, val) => setSelectedContract(val ? val.id : '')}
            getOptionLabel={(c) => c ? `${c.contract_number ? c.contract_number + ' · ' : ''}${c.project_address || c.project_name || '—'} · ${c.scope_title || c.title || 'Contract'} — ${fmt(c.contract_amount)}` : ''}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            filterOptions={(opts, state) => {
              const q = state.inputValue.toLowerCase().trim();
              if (!q) return opts;
              return opts.filter(c =>
                `${c.contract_number || ''} ${c.project_address || ''} ${c.project_name || ''} ${c.scope_title || ''} ${c.title || ''}`.toLowerCase().includes(q)
              );
            }}
            renderInput={(params) => <TextField {...params} label="Search contract # / address / scope" />}
            renderOption={(props, c) => (
              <li {...props} key={c.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1a237e' }}>{c.contract_number || 'Contract'}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#666' }}>{(c.project_address || c.project_name || '—')} · {(c.scope_title || c.title || 'Contract')} — {fmt(c.contract_amount)}</Typography>
                </Box>
              </li>
            )}
          />
        </Box>
      </Box>

      {/* Contract Summary */}
      {contract && (
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 3, mb: 3, bgcolor: '#f8f9ff' }}>
          <Typography variant="overline" sx={{ color: '#1a237e', fontWeight: 700, letterSpacing: 1, mb: 2, display: 'block' }}>Contract Summary</Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>Contract #</Typography>
              <Typography sx={{ color: '#1a237e', fontWeight: 800, fontSize: 22, mt: 0.5 }}>
                #{String(contract.id).slice(0, 8).toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>Address</Typography>
              <Typography sx={{ color: '#1a237e', fontWeight: 800, fontSize: 22, mt: 0.5, lineHeight: 1.2 }}>
                {contract.project_address || contract.project_name || '—'}
              </Typography>
              {contract.project_address && contract.project_name && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{contract.project_name}</Typography>
              )}
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #e8eaf6', display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => { setForm({...form, sequence_order: milestones.length+1}); setAddDialog(true); }}
              disabled={!selectedContract}
              sx={{ bgcolor: '#1a237e', borderRadius: 2, textTransform: 'none', px: 4, py: 1.25, fontSize: 15, fontWeight: 600 }}>
              Add Milestone
            </Button>
          </Box>
        </Card>
      )}

      {/* Summary Cards */}
      {contract && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Contract Value', value: fmt(totalValue), color: '#1a237e', sub: contract.scope_title || '—' },
            { label: 'Milestones Total', value: fmt(allocated), color: allocated > totalValue + 0.005 ? '#c62828' : '#0277bd', sub: `${fmt(remaining)} remaining of ${fmt(totalValue)}` },
            { label: 'Total Billed', value: fmt(totalBilled), color: '#7b1fa2', sub: `${pct}% of contract` },
            { label: 'Collected', value: fmt(totalPaid), color: '#2e7d32', sub: `${milestones.filter(m=>m.status==='paid').length} milestones paid` },
            { label: 'Outstanding', value: fmt(totalOutstanding), color: '#e65100', sub: `${milestones.filter(m=>m.status==='invoiced').length} invoices pending` },
          ].map(c => (
            <Grid item xs={12} sm={6} md={2.4} key={c.label}>
              <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>{c.label}</Typography>
                <Typography variant="h4" fontWeight={800} sx={{ color: c.color, my: 0.5 }}>{c.value}</Typography>
                <Typography variant="caption" color="text.secondary">{c.sub}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Progress Bar */}
      {contract && (
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={700} color="#1a237e">Payment Progress</Typography>
            <Typography variant="body2" fontWeight={700}>{fmt(totalBilled)} / {fmt(totalValue)}</Typography>
          </Box>
          <LinearProgress variant="determinate" value={pct}
            sx={{ height: 10, borderRadius: 5, bgcolor: '#e8eaf6',
              '& .MuiLinearProgress-bar': { bgcolor: pct >= 100 ? '#2e7d32' : '#1a237e', borderRadius: 5 } }} />
          <Typography variant="caption" color="text.secondary">{pct}% billed</Typography>
        </Card>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Milestones Table */}
      {selectedContract && (
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700} color="#1a237e">Payment Milestones</Typography>
            <Typography variant="caption" color="text.secondary">{milestones.length} milestones</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f6fa' }}>
                  {['','Milestone','Amount','Due','Status','Invoice / Action'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 13, color: '#555', fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {milestones.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No milestones yet.</Typography>
                      <Button startIcon={<AddIcon />} onClick={() => setAddDialog(true)} sx={{ mt: 1, color: '#1a237e' }}>
                        Add First Milestone
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
                {milestones.map((m, idx) => {
                  const s = STATUS[m.status] || STATUS.pending;
                  const isOverdue = m.due_date && new Date(m.due_date) < new Date() && !['paid','invoiced'].includes(m.status);
                  const expanded = !!expandedRows[m.id];
                  const canSend = !!(m.recipient_email || m.customer_email);
                  const isSent = !!m.sent_at;
                  const toggle = () => setExpandedRows(r => ({ ...r, [m.id]: !r[m.id] }));
                  const openInvoiceDialog = () => setInvoiceSuccess({
                    invoice_id: m.invoice_id,
                    invoice_number: m.invoice_number,
                    invoice_document_url: m.invoice_document_url,
                    customer_email: m.customer_email || null,
                    recipient_email: m.recipient_email || m.customer_email || null,
                    sending: false, sent: isSent,
                  });
                  return (
                    <React.Fragment key={m.id}>
                    <TableRow sx={{ '&:hover': { bgcolor: '#f9f9ff' }, bgcolor: isOverdue ? '#fff8f8' : 'white', cursor: 'pointer' }}
                      onClick={(e) => { if (e.target.closest('button') || e.target.closest('a') || e.target.closest('.MuiSelect-root') || e.target.closest('[role=button]')) return; toggle(); }}>
                      <TableCell sx={{ width: 34, color: '#999', verticalAlign: 'top', pr: 0 }}>
                        {expanded ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', maxWidth: 240 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {(m.sequence_order || idx+1)}. {m.title}
                        </Typography>
                        {m.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.description}</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#1a237e', fontSize: 15, verticalAlign: 'top' }}>{fmt(m.payment_amount)}</TableCell>
                      <TableCell sx={{ fontSize: 12, verticalAlign: 'top', color: isOverdue ? '#c62828' : 'inherit', fontWeight: isOverdue ? 700 : 400 }}>
                        {isOverdue ? '⚠️ ' : ''}{fmtDate(m.due_date)}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Chip label={s.label} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 10 }}/>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, verticalAlign: 'top' }}>
                        {m.invoice_number ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Link component="button" type="button" onClick={openInvoiceDialog}
                                sx={{ fontSize: 12, fontWeight: 700, color: '#1a237e', textDecoration: 'underline', cursor: 'pointer' }}>
                                {m.invoice_number}
                              </Link>
                              <Chip label={m.invoice_status} size="small"
                                sx={{ fontSize: 9, bgcolor: STATUS[m.invoice_status]?.bg, color: STATUS[m.invoice_status]?.color }}/>
                            </Box>
                            {canSend && (isSent ? (
                              <Typography sx={{ fontSize: 11, color: '#2e7d32', mt: 0.5 }}>✓ Invoice sent</Typography>
                            ) : (
                              <Button size="small" variant="contained" startIcon={<SendIcon sx={{ fontSize: 13 }} />}
                                onClick={openInvoiceDialog}
                                sx={{ fontSize: 10, bgcolor: '#1a237e', borderRadius: 2, mt: 0.5, py: 0.25 }}>
                                Send Invoice
                              </Button>
                            ))}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {m.status !== 'invoiced' && m.status !== 'paid' && m.status !== 'completed' && (
                              <TextField select size="small" value={m.status}
                                onClick={(e) => e.stopPropagation()}
                                onChange={async e => {
                                  const newStatus = e.target.value;
                                  if (newStatus === 'invoiced') { await handleIssueInvoice(m.id); }
                                  else { await api.put(`/milestones/${m.id}/status`, {status: newStatus}); setMilestones(ms => ms.map(x => x.id===m.id ? {...x, status: newStatus} : x)); }
                                }}
                                sx={{ minWidth: 130, fontSize: 13 }}>
                                <MenuItem value="pending">Not Started</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                              </TextField>
                            )}
                            {m.status === 'completed' && (
                              <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); setInvTerms('net_30'); setTermsDialog({ open: true, milestoneId: m.id }); }}
                                startIcon={<ReceiptIcon sx={{ fontSize: 14 }} />}
                                sx={{ fontSize: 11, bgcolor: '#1a237e', borderRadius: 2 }}>Create Invoice</Button>
                            )}
                            {['pending','in_progress'].includes(m.status) && (
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(m.id); }} sx={{ color: '#e53935' }}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: '#f7f8fc', py: 1.5, pl: 5 }}>
                          <Typography sx={{ fontSize: 13 }}><b>Title:</b> {m.title}</Typography>
                          {m.description
                            ? <Typography sx={{ fontSize: 13, mt: 0.5 }}><b>Description:</b> {m.description}</Typography>
                            : <Typography sx={{ fontSize: 12, color: '#999', mt: 0.5 }}>No description</Typography>}
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {!selectedContract && (
        <Card elevation={0} sx={{ border: '1px dashed #c5cae9', borderRadius: 3, p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No signed contracts found.</Typography>
          <Typography variant="caption" color="text.secondary">Contracts must be active/completed to manage payment milestones.</Typography>
        </Card>
      )}

      {/* Add Milestone Dialog */}
      <Dialog open={termsDialog.open || !!invoiceSuccess} onClose={() => { setTermsDialog({ open: false, milestoneId: null }); setInvoiceSuccess(null); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>Create Invoice</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {invoiceSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32', mb: 1 }}>Invoice Created</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 3 }}>{invoiceSuccess.invoice_number}</Typography>
              {invoiceSuccess.sent ? (
                <Box sx={{ bgcolor: '#e8f5e9', border: '1px solid #66bb6a', borderRadius: 2, p: 2.5, mb: 1, display: 'inline-block', minWidth: 320 }}>
                  <Typography sx={{ color: '#2e7d32', fontSize: 22, fontWeight: 800, mb: 0.5 }}>✓ Email Sent</Typography>
                  <Typography sx={{ color: '#1b5e20', fontSize: 14 }}>Sent to <b>{invoiceSuccess.recipient_email || invoiceSuccess.customer_email}</b></Typography>
                  <Typography sx={{ color: '#1b5e20', fontSize: 13, mt: 0.5 }}>You were CC'd; PDF attached.</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {invoiceSuccess.invoice_document_url && (
                    <Button variant="outlined" href={invoiceSuccess.invoice_document_url} target="_blank"
                      sx={{ borderRadius: 2, py: 1.25, px: 3, minWidth: 140 }}>View PDF</Button>
                  )}
                  {(invoiceSuccess.recipient_email || invoiceSuccess.customer_email) ? (
                    <Button variant="contained" disabled={invoiceSuccess.sending}
                      onClick={async () => {
                        setInvoiceSuccess(s => ({ ...s, sending: true }));
                        try {
                          const r = await api.post('/invoices/' + invoiceSuccess.invoice_id + '/send-to-customer');
                          setInvoiceSuccess(s => ({ ...s, sending: false, sent: true }));
                          setSnack({ open: true, message: 'Invoice emailed to customer (you CC\'d)', severity: 'success' });
                        } catch (err) {
                          setInvoiceSuccess(s => ({ ...s, sending: false }));
                          setSnack({ open: true, message: err.response?.data?.error || 'Failed to send', severity: 'error' });
                        }
                      }}
                      sx={{ bgcolor: '#1a237e', borderRadius: 2, py: 1.25, px: 3, minWidth: 240 }}>
                      {invoiceSuccess.sending ? 'Sending...' : 'Send Invoice'}
                    </Button>
                  ) : (
                    <Typography sx={{ color: '#888', fontSize: 13 }}>No recipient email on file for this contract</Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : (
          <>
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>Select payment terms for this invoice.</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="payment-terms-label" sx={{ fontWeight: 600 }}>Payment Terms</InputLabel>
            <Select
              labelId="payment-terms-label"
              value={invTerms}
              label="Payment Terms"
              onChange={e => setInvTerms(e.target.value)}
              sx={{
                fontSize: 16, fontWeight: 600,
                '& .MuiSelect-select': { py: 1.75 },
              }}
            >
              <MenuItem value="immediate" sx={{ fontSize: 15 }}>Immediate (due on receipt)</MenuItem>
              <MenuItem value="net_7" sx={{ fontSize: 15 }}>Net 7</MenuItem>
              <MenuItem value="net_15" sx={{ fontSize: 15 }}>Net 15</MenuItem>
              <MenuItem value="net_30" sx={{ fontSize: 15 }}>Net 30</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ bgcolor: '#f0f4ff', borderRadius: 2, p: 1.5, mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#1a237e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Due Date</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1a237e', mt: 0.25 }}>
              {(() => {
                const daysMap = { immediate: 0, net_7: 7, net_15: 15, net_30: 30 };
                const d = new Date();
                d.setDate(d.getDate() + (daysMap[invTerms] ?? 30));
                return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
              })()}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#888' }}>
            Preview opens a sample PDF in a new tab. The final invoice number is assigned when you submit.
          </Typography>
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          {invoiceSuccess ? (
            <Button variant="contained" onClick={() => { setInvoiceSuccess(null); setTermsDialog({ open: false, milestoneId: null }); }} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Close</Button>
          ) : (<>
          <Button onClick={() => setTermsDialog({ open: false, milestoneId: null })}>Cancel</Button>
          <Button variant="outlined"
            onClick={() => handlePreviewInvoice(termsDialog.milestoneId, invTerms)}
            sx={{ borderColor: '#1a237e', color: '#1a237e', borderRadius: 2, px: 3 }}>
            Preview
          </Button>
          <Button variant="contained" disabled={saving}
            onClick={async () => {
              const mid = termsDialog.milestoneId;
              await handleIssueInvoice(mid, invTerms);
            }}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            Create Invoice
          </Button>
          </>)}
        </DialogActions>
      </Dialog>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>Add Payment Milestone</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField label="Milestone Title *" value={form.title} onChange={e => setForm({...form, title: e.target.value})} fullWidth placeholder="e.g. Foundation Complete, Framing Done" />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Order #" type="number" value={form.sequence_order} onChange={e => setForm({...form, sequence_order: e.target.value})} fullWidth />
              </Grid>
            </Grid>
            <TextField label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} fullWidth multiline rows={2} />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.amount_mode}
              onChange={(e, v) => { if (v) setForm({ ...form, amount_mode: v }); }}
              sx={{ alignSelf: 'flex-start' }}
            >
              <ToggleButton value="fixed" sx={{ textTransform: 'none', px: 2 }}>Fixed amount</ToggleButton>
              <ToggleButton value="pct" sx={{ textTransform: 'none', px: 2 }}>% of contract</ToggleButton>
            </ToggleButtonGroup>
            <Grid container spacing={2}>
              {form.amount_mode === 'fixed' ? (
                <Grid item xs={6}>
                  <TextField label="Payment Amount ($) *" type="number" value={form.payment_amount}
                    onChange={e => setForm({...form, payment_amount: e.target.value})} fullWidth />
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <TextField label="Percent of contract (%) *" type="number" value={form.pct}
                    onChange={e => setForm({...form, pct: e.target.value})} fullWidth
                    InputProps={{ endAdornment: <span style={{ color: '#888' }}>%</span> }}
                    helperText={form.pct && totalValue > 0
                      ? `= ${fmt((totalValue * Number(form.pct || 0)) / 100)} of ${fmt(totalValue)}`
                      : `Contract total: ${fmt(totalValue)}`} />
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField label="Estimated Completion Date" type="date" value={form.due_date}
                  onChange={e => setForm({...form, due_date: e.target.value})} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: wouldExceed ? '#fdecea' : '#f0f4ff' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: wouldExceed ? '#c62828' : '#1a237e' }}>
                Allocated {fmt(allocated)} of {fmt(totalValue)} · Remaining {fmt(remaining)}
              </Typography>
              {newAmount > 0 && (
                <Typography sx={{ fontSize: 12, color: wouldExceed ? '#c62828' : '#555', mt: 0.25 }}>
                  This milestone: {fmt(newAmount)}{wouldExceed ? ' — exceeds remaining contract value' : ''}
                </Typography>
              )}
            </Box>
          
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
</Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving || !form.title || (form.amount_mode === 'fixed' ? !form.payment_amount : !form.pct) || wouldExceed}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            {saving ? 'Saving...' : 'Add Milestone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
